import isEmpty from "lodash/isEmpty";
import {ReactNode, useEffect, useMemo, useState} from "react";
import {useFormContext} from "react-hook-form";

import Button from "@/components/button/Button.tsx";
import {FormActionsBar} from "@/components/form/FormActionsBar.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {useUnsavedChangesNavigationBlocker} from "@/components/form/hooks/useUnsavedChangesNavigationBlocker.tsx";
import {RequiredFieldsNote} from "@/components/form/requiredFieldsNote/RequiredFieldsNote.tsx";
import {TabContentContainer} from "@/components/layout/TabContentContainer.tsx";
import {BaseTabContentLayoutProps, TabSection} from "@/components/layout/TabContentContainer.types.ts";
import {TabModalContainer} from "@/components/layout/TabModalContainer.tsx";
import {UnsavedIndicator} from "@/components/unsavedIndicator/UnsavedIndicator.tsx";
import {ServerErrors} from "@/lib/types/common.ts";

interface TabFormProps extends BaseTabContentLayoutProps {
  goBack: () => void;
  serverErrorsState?: ServerErrors;
  formId: string;
  fieldToTabMap: Record<string, string>;
  variant?: "default" | "modal";
  submitText?: string;
  prevalidationFields?: string[];
  actionsBar?: ReactNode;
  hasRequiredFields?: boolean;
}

export const TabForm = ({
  sections,
  goBack,
  children,
  title = "",
  tab,
  setTab,
  serverErrorsState,
  formId,
  fieldToTabMap,
  prevalidationFields,
  variant = "default",
  className,
  submitText = "Create",
  actionsBar,
  headerClassName,
  hasRequiredFields = true,
}: TabFormProps) => {
  const {formState, trigger} = useFormContext();
  const [tabErrorState, setTabErrorState] = useState<Record<string, boolean>>({});
  const {UnsavedChangesModal, blockOrDiscard} = useUnsavedChangesNavigationBlocker({onDiscard: goBack});

  useEffect(() => {
    if (!isEmpty(prevalidationFields)) {
      trigger(prevalidationFields);
    }
    // Only trigger when the actual field names change, not the array reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, JSON.stringify(prevalidationFields)]);

  useEffect(() => {
    const newTabErrorState: Record<string, boolean> = {};
    Object.keys(sections).forEach((sectionKey) => {
      newTabErrorState[sectionKey] = false;
    });
    setTabErrorState(newTabErrorState);
  }, [sections, serverErrorsState, tab]);

  useEffect(() => {
    const errorKeys = Object.keys(formState.errors);
    const newTabsWithErrorSet: Set<string> = new Set();
    errorKeys.forEach((key) => {
      const fieldTab = fieldToTabMap[key] || "";
      if (fieldTab) {
        newTabsWithErrorSet.add(fieldTab);
        return;
      }
      console.warn(`No tab found for field: ${key}`);
    });

    let hasChanged = false;
    Object.keys(tabErrorState).find((tabKey) => {
      const shouldHaveError = newTabsWithErrorSet.has(tabKey);
      if (tabErrorState[tabKey] !== shouldHaveError) {
        hasChanged = true;
        return true;
      }
    });
    if (!hasChanged) {
      return;
    }

    setTabErrorState((prevState) => {
      const updatedState = {...prevState};
      Object.keys(updatedState).forEach((tabKey) => {
        updatedState[tabKey] = newTabsWithErrorSet.has(tabKey);
      });
      return updatedState;
    });
  }, [fieldToTabMap, formState, serverErrorsState, tabErrorState]);

  const sectionsWithErrors: Record<string, TabSection> = useMemo(() => {
    const derivedSections: Record<string, TabSection> = sections;
    Object.keys(derivedSections).forEach((key) => {
      const tabKey = key;
      derivedSections[tabKey] = {
        ...derivedSections[tabKey],
        error: tabErrorState[tabKey],
      };
    });
    return derivedSections;
  }, [sections, tabErrorState]);

  return (
    <>
      {variant === "modal" ? (
        <TabModalContainer
          title={
            <div className={"d-flex justify-content-between w-100"}>
              <div className="pulseTextH2Bold pulse-color-gray-800">{title}</div>
              <div className="d-flex flex-column flex-md-row gap-3 align-items-center flex-wrap justify-content-md-end justify-content-center">
                {formState.isDirty && <UnsavedIndicator />}
              </div>
            </div>
          }
          onHide={blockOrDiscard}
          sections={sectionsWithErrors}
          tab={tab}
          setTab={setTab}
          className={className}
          headerClassName={headerClassName}
          bottomActionsBar={
            actionsBar || (
              <div className="d-flex align-items-center justify-content-md-end justify-content-center gap-2">
                <Button variant="secondary" onClick={blockOrDiscard}>
                  Cancel
                </Button>
                <Button className="text-nowrap" type="submit" form={formId} disabled={!formState.isDirty}>
                  {submitText}
                </Button>
              </div>
            )
          }
        >
          {serverErrorsState?.__all__ && <FormErrorAlerts errors={serverErrorsState?.__all__} className="mb-4" />}
          {hasRequiredFields && <RequiredFieldsNote />}
          {children}
        </TabModalContainer>
      ) : (
        <TabContentContainer
          sections={sectionsWithErrors}
          title={title}
          tab={tab}
          setTab={setTab}
          className={className}
          headerClassName={headerClassName}
          actionsBar={actionsBar || <FormActionsBar formId={formId} submitText={submitText} onCancel={goBack} />}
        >
          {serverErrorsState?.__all__ && <FormErrorAlerts errors={serverErrorsState?.__all__} className="mb-4" />}
          {hasRequiredFields && <RequiredFieldsNote />}
          {children}
        </TabContentContainer>
      )}
      <UnsavedChangesModal />
    </>
  );
};
